type OwnershipLike = {
  organizationID?: string | null
  organizationMasterID?: string | null
}

type TeamLike = {
  id?: string | null
  masterID?: string | null
  isMaster?: boolean | null
}

type OrganizationRelationLike = Pick<TeamLike, 'id'>

type ResolveOrganizationScopeParams<TTeam extends TeamLike = TeamLike> = {
  ownership?: OwnershipLike | null
  selectedOrganizationId?: string | null
  selectedOrganization?: TTeam | null
  selectedOrganizationParent?: TTeam | null
  isSolo?: boolean
  personalWorkspaceOrganizationId?: string | null
}

export type OrganizationScopeKind = 'organization' | 'solo' | 'unresolved'

export type OrganizationScope<TTeam extends TeamLike = TeamLike> = {
  scopeKind: OrganizationScopeKind
  selectedOrganizationId: string | null
  activeOrganizationId: string | undefined
  activeOrganizationMasterId: string | undefined
  workspaceOrganizationId?: string | undefined
  selectedOrganization: TTeam | undefined
  selectedOrganizationParent: TTeam | undefined
  isSolo: boolean
}

const getNonEmptyString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.length > 0 ? value : undefined

export const resolveOrganizationScope = <TTeam extends TeamLike = TeamLike>({
  ownership,
  selectedOrganizationId,
  selectedOrganization,
  selectedOrganizationParent,
  isSolo = false,
  personalWorkspaceOrganizationId,
}: ResolveOrganizationScopeParams<TTeam>): OrganizationScope<TTeam> => {
  const resolvedSelectedOrganizationId =
    selectedOrganizationId ??
    getNonEmptyString(selectedOrganization?.id) ??
    null

  const activeOrganizationId = isSolo
    ? undefined
    : (getNonEmptyString(ownership?.organizationID) ??
      getNonEmptyString(resolvedSelectedOrganizationId) ??
      getNonEmptyString(selectedOrganization?.id))

  const activeOrganizationMasterId = isSolo
    ? undefined
    : (getNonEmptyString(ownership?.organizationMasterID) ??
      getNonEmptyString(selectedOrganization?.masterID) ??
      (selectedOrganization?.isMaster
        ? getNonEmptyString(selectedOrganization?.id)
        : undefined))

  const scopeKind: OrganizationScopeKind = isSolo
    ? 'solo'
    : activeOrganizationId
      ? 'organization'
      : 'unresolved'

  const workspaceOrganizationId =
    activeOrganizationId ?? getNonEmptyString(personalWorkspaceOrganizationId)

  return {
    scopeKind,
    selectedOrganizationId: resolvedSelectedOrganizationId,
    activeOrganizationId,
    activeOrganizationMasterId,
    workspaceOrganizationId,
    selectedOrganization: selectedOrganization ?? undefined,
    selectedOrganizationParent: selectedOrganizationParent ?? undefined,
    isSolo,
  }
}

export const resolveInheritedOrganizationIds = ({
  scopeKind,
  activeOrganizationId,
  activeOrganizationMasterId,
  selectedOrganizationParent,
}: {
  scopeKind?: OrganizationScopeKind
  activeOrganizationId?: string
  activeOrganizationMasterId?: string
  selectedOrganizationParent?: OrganizationRelationLike | null
}): string[] => {
  if (
    scopeKind !== 'organization' ||
    !getNonEmptyString(activeOrganizationId)
  ) {
    return []
  }

  const resolvedActiveOrganizationId = getNonEmptyString(activeOrganizationId)

  return Array.from(
    new Set(
      [
        getNonEmptyString(selectedOrganizationParent?.id),
        getNonEmptyString(activeOrganizationMasterId),
      ].filter((organizationId): organizationId is string =>
        Boolean(
          organizationId && organizationId !== resolvedActiveOrganizationId,
        ),
      ),
    ),
  )
}

type ResolveActiveOrganizationIdParams = {
  ownership?: OwnershipLike | null
  team?: TeamLike | null
}

export const isRotorTemplate = (template: string | null | undefined): boolean =>
  template === 'rotor'

export const resolveActiveOrganizationId = ({
  ownership,
  team,
}: ResolveActiveOrganizationIdParams): string | undefined =>
  resolveOrganizationScope({
    ownership,
    selectedOrganization: team,
  }).activeOrganizationId
